import type { ModalFuncProps, ModalProps } from 'antd';
import { Modal } from 'antd';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';

// 扩展 Modal 配置接口
/**
 * 自定义模态框配置接口，继承自ModalProps
 * 扩展了更多自定义属性以满足不同场景下的模态框需求
 */
interface CustomModalConfig extends ModalProps {
  width?: number | string; // 模态框的宽度，可以是数字或字符串
  maskClosable?: boolean; // 点击遮罩层是否可关闭模态框
  centered?: boolean; // 是否居中显示
  okText?: string; // 确认按钮的文字
  cancelText?: string; // 取消按钮的文字
  closable?: boolean; // 是否显示右上角的关闭按钮
  footer?: ReactNode; // 自定义页脚内容
  size?: 'normal' | 'large'; // 模态框大小，分为普通和大两种尺寸
  style?: React.CSSProperties; // 模态框的自定义样式
}

// Modal 方法类型
/**
 * ModalMethods 接口定义了模态框的各种操作方法
 * 这些方法用于创建和显示不同类型的模态框
 */
export interface ModalMethods {
  /**
   * 显示信息类型的模态框
   * @param config 模态框的配置对象，包含标题、内容等属性
   */
  info: (config: ModalConfig) => ModalReturn | void;
  /**
   * 显示成功类型的模态框
   * @param config 模态框的配置对象，包含标题、内容等属性
   */
  success: (config: ModalConfig) => ModalReturn | void;
  /**
   * 显示错误类型的模态框
   * @param config 模态框的配置对象，包含标题、内容等属性
   */
  error: (config: ModalConfig) => ModalReturn | void;
  /**
   * 显示警告类型的模态框
   * @param config 模态框的配置对象，包含标题、内容等属性
   */
  warning: (config: ModalConfig) => ModalReturn | void;
  /**
   * 显示确认类型的模态框，通常包含确认和取消按钮
   * @param config 模态框的配置对象，包含标题、内容、确认回调等属性
   */
  confirm: (config: ModalConfig) => ModalReturn | void;
  /**
   * 显示详情类型的模态框，用于展示详细信息
   * @param config 模态框的配置对象，包含标题、内容等属性
   */
  detail: (config: ModalConfig) => ModalReturn | void;
  /**
   * 销毁当前显示的模态框，将其从DOM中移除
   */
  destroy: () => void;
}

/**
 * ModalConfig 接口，用于定义模态框的配置属性
 * 继承自 CustomModalConfig 接口，扩展了通用的模态框配置选项
 */
interface ModalConfig extends CustomModalConfig {
  title?: ReactNode; // 模态框的标题，可以是任何可渲染的 React 节点
  className?: string; // 模态框的自定义 CSS 类名，用于样式定制
  content: ReactNode; // 模态框的内容，必须是可渲染的 React 节点，必填属性
  okButtonProps?: any; // 确认按钮的额外属性，可以是任意类型
  cancelButtonProps?: any; // 取消按钮的额外属性，可以是任意类型
}

// Modal 拦截器类型
export interface ModalInterceptor {
  (config: ModalConfig, type: keyof ModalMethods): boolean | ModalConfig;
}

interface ModalReturn {
  destroy: () => void;
  update: (configUpdate: ModalFuncProps | ((prevConfig: ModalFuncProps) => ModalFuncProps)) => void;
}

// 默认配置
const DEFAULT_MODAL_CONFIG = {
  width: 600,
  maskClosable: false,
  centered: true,
  okText: '确定',
  showIcon: false,
  cancelText: '取消',
  wrapClassName: 'jf-modal-wrap',
  // 目前有两种弹窗样式，白色的顶部 jf-modal-custom, 非白色的顶部 jf-modal
  className: 'jf-modal',
  icon: null,
  autoFocusButton: null,
};

// 独立的全局 Modal 工具类
/**
 * 全局模态框工具类，采用单例模式实现全局统一的模态框管理
 */
class GlobalModalTool {
  private static instance: GlobalModalTool; // 单例实例
  /**
   * 获取 GlobalModalTool 的单例实例
   * @returns GlobalModalTool 的单例实例
   */
  static getInstance(): GlobalModalTool {
    if (!GlobalModalTool.instance) {
      GlobalModalTool.instance = new GlobalModalTool();
    }
    return GlobalModalTool.instance;
  }
  modal: ModalReturn | null = null; // 当前模态框实例
  private interceptors: ModalInterceptor[] = []; // 拦截器数组
  private defaultConfig: CustomModalConfig = DEFAULT_MODAL_CONFIG; // 默认配置

  /**
   * 添加模态框拦截器
   * @param interceptor 拦截器函数
   */
  addInterceptor(interceptor: ModalInterceptor) {
    this.interceptors.push(interceptor);
  }

  /**
   * 设置默认配置
   * @param config 默认配置对象
   */
  setDefaultConfig(config: CustomModalConfig) {
    this.defaultConfig = { ...this.defaultConfig, ...config };
  }
  /**
   * 清除所有拦截器
   */
  clearInterceptors() {
    this.interceptors = [];
  }
  /**
   * 销毁当前模态框
   */
  destroy = () => this.modal?.destroy();
  /**
   * 显示信息模态框
   *
   * @param config 模态框配置参数
   */
  info = (config: ModalConfig) => {
    return this.handleModal('info', config);
  };

  /**
   * 显示成功模态框
   * @param config 模态框配置参数
   */
  success = (config: ModalConfig) => {
    return this.handleModal('success', config);
  };

  /**
   * 显示错误模态框
   * @param config 模态框配置参数
   */
  error = (config: ModalConfig) => {
    return this.handleModal('error', config);
  };

  /**
   * 显示警告模态框
   * @param config 模态框配置参数
   */
  warning = (config: ModalConfig) => {
    return this.handleModal('warning', config);
  };

  /**
   * 显示确认模态框
   * @param config 模态框配置参数
   */
  confirm = (config: ModalConfig) => {
    return this.handleModal('confirm', { closable: true, ...config });
  };

  /**
   * 显示详情模态框
   * @param config 模态框配置参数
   */
  detail = (config: ModalConfig) => {
    return this.handleModal('confirm', { closable: true, footer: null, ...config });
  };
  /**
   * 处理模态框显示的核心方法
   * @param type 模态框类型
   * @param config 模态框配置参数
   */
  private handleModal(type: keyof ModalMethods, config: ModalConfig) {
    let finalConfig = { ...this.defaultConfig, ...config };
    // 处理 size 属性，根据不同的 size 设置对应的 width
    if (finalConfig.size === 'large') {
      finalConfig.width = 800;
    } else if (finalConfig.size === 'normal' || !finalConfig.width) {
      finalConfig.width = 600;
    }

    // 执行拦截器，可以修改配置或阻止模态框显示
    for (const interceptor of this.interceptors) {
      const result = interceptor(finalConfig, type);
      if (result === false) {
        return; // 拦截器返回 false 时阻止 Modal 显示
      }
      if (typeof result === 'object') {
        finalConfig = { ...finalConfig, ...result };
      }
    }
    // 调用 Modal 方法
    // @ts-ignore
    this.modal = Modal[type](finalConfig);
    return this.modal;
  }
}

// 创建全局实例
const globalModalInstance = GlobalModalTool.getInstance();

// 创建 Modal API
const modalApi = {
  info: globalModalInstance.info,
  success: globalModalInstance.success,
  error: globalModalInstance.error,
  warning: globalModalInstance.warning,
  confirm: globalModalInstance.confirm,
  detail: globalModalInstance.detail,
  destroy: globalModalInstance.destroy,
};

// 创建上下文
const ModalContext = createContext<ModalMethods | null>(null);

interface ModalProviderProps {
  children: ReactNode;
  interceptors?: ModalInterceptor[];
  defaultConfig?: CustomModalConfig;
}

// Modal 提供者组件
export const ModalProvider: React.FC<ModalProviderProps> = ({
  children,
  interceptors = [],
  defaultConfig = {},
}) => {
  // 同步拦截器和配置到全局实例
  React.useEffect(() => {
    // 清除之前的拦截器
    globalModalInstance.clearInterceptors();

    // 添加新的拦截器
    interceptors.forEach((interceptor) => {
      globalModalInstance.addInterceptor(interceptor);
    });

    // 设置默认配置
    globalModalInstance.setDefaultConfig({
      ...DEFAULT_MODAL_CONFIG,
      ...defaultConfig,
    });
  }, [interceptors, defaultConfig]);

  // 创建封装后的 Modal API
  const customModalApi = useMemo<ModalMethods>(
    () => ({
      info: (config: ModalConfig) => globalModalInstance.info(config),
      success: (config: ModalConfig) => globalModalInstance.success(config),
      error: (config: ModalConfig) => globalModalInstance.error(config),
      warning: (config: ModalConfig) => globalModalInstance.warning(config),
      confirm: (config: ModalConfig) => globalModalInstance.confirm(config),
      detail: (config: ModalConfig) => globalModalInstance.detail(config),
      destroy: () => globalModalInstance.destroy(),
    }),
    [],
  );
  return <ModalContext.Provider value={customModalApi}>{children}</ModalContext.Provider>;
};

// 使用 Modal 的 Hook
export const useModal = (): ModalMethods => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

// 导出全局实例和 API
export { globalModalInstance, modalApi };
